import { usePageDrawer } from '@app/components/page/drawer/PageDrawerContext';
import { Box } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';

import { LiveTopicsPanelCategoryToolbar } from './category-toolbar/LiveTopicsPanelCategoryToolbar';
import { LiveTopicsDetailView } from './detail-view/LiveTopicsDetailView';
import { LiveTopicsPanelHeader } from './header/LiveTopicsPanelHeader';
import { useLiveTopicsPanelState } from './hooks/useLiveTopicsPanelState';
import { LiveTopicsPanelListView } from './list-view/LiveTopicsPanelListView';

const panelTitleMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.panel.title',
  defaultMessage: 'Live Topics',
});

const newTopicsPillMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.panel.newTopics',
  defaultMessage: '{count, plural, one {# tema nuevo} other {# temas nuevos}}',
});

const noTopicsMessage = defineMessage({
  id: 'logger.streamScreen.liveTopics.noTopicsForRange',
  defaultMessage: 'No hay temas para el rango seleccionado.',
});

/** Shared layout for panel root: full-height column with hidden overflow. */
const panelRootSx = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
} as const;

/**
 * Live Topics drawer panel: list view (divider-separated items, scroll-lock, "N Temas Nuevos" pill)
 * and detail view (popover-like content + Previous/Next). Uses latest topics API with polling.
 */
export function LiveTopicsPanel() {
  const { closeDrawer } = usePageDrawer();
  const { $t } = useIntl();

  const { topics, filters, list, newTopics, pagination, scroll, selection } =
    useLiveTopicsPanelState();

  const header = (
    <LiveTopicsPanelHeader
      title={$t(panelTitleMessage)}
      isDetailView={selection.isDetailView}
      onBack={selection.handleBack}
      onClose={closeDrawer}
    />
  );

  /** List and detail are always in the tree; we hide the inactive one with CSS so the list never unmounts when opening detail (avoids flash on back). */
  const showList = !selection.isDetailView;
  const showDetail = selection.isDetailView && selection.selectedTopic != null;

  return (
    <Box sx={panelRootSx}>
      {header}
      <Box
        key="live-topics-list"
        sx={{
          display: showList ? 'flex' : 'none',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <LiveTopicsPanelCategoryToolbar
          filters={{
            categoryOptions: filters.categoryOptions,
            selectedCategories: filters.selectedCategories,
            onSelectedCategoriesChange: filters.setSelectedCategories,
            textFilter: filters.textFilter,
            onTextFilterChange: filters.setTextFilter,
          }}
        />
        <LiveTopicsPanelListView
          scroll={{
            scrollableRef: scroll.scrollableRef,
            onScrollToTop: newTopics.resetCount,
          }}
          list={{
            isLoading: list.isLoading,
            filteredTopics: list.filteredTopics,
            emptyMessage: $t(noTopicsMessage),
            newlyArrivedTopicIds: list.newlyArrivedTopicIds,
            viewedInDetailTopicIds: list.viewedInDetailTopicIds,
            onTopicClick: selection.setSelectedTopicId,
            hasActiveFilters: filters.hasActiveFilters,
          }}
          newTopics={{
            label: $t(newTopicsPillMessage, { count: newTopics.count }),
            count: newTopics.count,
          }}
          pagination={{
            hasMoreOlder: pagination.hasMoreOlder,
            isLoadingMore: pagination.isLoadingMore,
            onLoadMore: pagination.loadMoreOlder,
          }}
        />
      </Box>
      {selection.selectedTopic && (
        <Box
          key="live-topics-detail"
          sx={{
            display: showDetail ? 'flex' : 'none',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <LiveTopicsDetailView
            topic={selection.selectedTopic}
            topics={topics}
            onSelectTopic={selection.setSelectedTopicId}
            onClose={closeDrawer}
          />
        </Box>
      )}
    </Box>
  );
}
